import React, { useContext, useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { MenuContext } from "../../widgets/Menu/context";
import { NavLive, NavLiveLogo } from "./types";

/**
 * The live slot of a nav tab (Competitions / Compete, Bez 2026-10-07; it replaced the 2026-10-06 live dot): the token logos of what is
 * live now, each in a green "live" ring, up to three overlapping and "+N" beyond. The logo area is its own link (one live item: its
 * page; several: the list), a sibling of the tab's link, never inside it.
 *
 * The slot keeps its size whether nothing, one or many things are live, so the tab label never moves:
 *  - "bar" (top bar): a fixed-width slot after the label; empty while nothing is live.
 *  - "icon" (phone bar): laid over the tab's fixed icon box; while something is live the logos stand in for the tab's icon (the
 *    caller hides the icon, keeping its box), and the cluster stays inside the tab's own column.
 */
export const LIVE_LOGO_PX = { bar: 20, icon: 20 } as const;
/** horizontal step between overlapping logos */
const STEP_PX = 13;
/** the reserved width of the top-bar slot: three logos (46 px) plus a one-digit "+N" chip (measured 67 px in all) */
export const LIVE_SLOT_BAR_PX = 68;
export const LIVE_MAX_LOGOS = 3;

const GREEN = "57, 217, 138"; // theme.colors.success in the dark theme, for the glow's alpha

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.75; }
  70%, 100% { transform: scale(1.75); opacity: 0; }
`;

const Slot = styled.span<{ $place: "bar" | "icon" }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  ${({ $place }) =>
    $place === "bar"
      ? css`
          /* reserved: the same width with 0, 1, 3 or more live, so nothing after it moves either */
          width: ${LIVE_SLOT_BAR_PX}px;
          height: 28px;
          /* with the tab's own 10 px padding: the same 14 px gap before the logos and after a full slot */
          margin: 0 4px;
        `
      : css`
          /* over the tab's 24 px icon box (4 px under the item's top edge), centred in the column */
          position: absolute;
          z-index: 7;
          top: 4px;
          left: 50%;
          transform: translateX(-50%);
          height: 24px;
          justify-content: center;
          pointer-events: none;
        `}
`;

const Cluster = styled.a`
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 0;
  border-radius: 999px;
  text-decoration: none;
  pointer-events: auto;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: transform 160ms cubic-bezier(0.22, 1, 0.36, 1), filter 160ms ease-out;
  /* a thumb-sized hit area around small logos, kept off the label underneath */
  &::before {
    content: "";
    position: absolute;
    inset: -4px -10px 0;
    border-radius: 14px;
  }
  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.12) saturate(1.1);
  }
  &:active {
    transform: translateY(0) scale(0.96);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.success};
    outline-offset: 4px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover,
    &:active {
      transform: none;
    }
  }
`;

const Logo = styled.span<{ $size: number; $i: number }>`
  position: relative;
  flex: none;
  display: inline-grid;
  place-items: center;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  margin-left: ${({ $i, $size }) => ($i === 0 ? 0 : STEP_PX - $size)}px;
  /* the first (most urgent) logo sits on top */
  z-index: ${({ $i }) => 3 - $i};
  border-radius: 50%;
  background: radial-gradient(120% 120% at 30% 20%, #2a3a86 0%, #150f4a 60%, #0a0e22 100%);
  /* green live ring, a hairline in the bar colour that cuts the overlap, the live glow, and a drop for depth */
  box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.success}, 0 0 0 3.5px ${({ theme }) => theme.colors.backgroundAlt},
    0 0 10px 2px rgba(${GREEN}, 0.45), 0 3px 6px 1px rgba(0, 0, 0, 0.5);
  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    display: block;
  }
  /* a soft top-left highlight: the logo reads as a lit coin, not a flat sticker */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: linear-gradient(155deg, rgba(255, 255, 255, 0.32) 0%, rgba(255, 255, 255, 0) 46%);
    pointer-events: none;
  }
`;

/** the live pulse: one green ring breathing out of the front logo */
const Pulse = styled.span`
  position: absolute;
  inset: -2px;
  border-radius: 50%;
  border: 2px solid rgba(${GREEN}, 0.9);
  pointer-events: none;
  animation: ${pulse} 2s cubic-bezier(0.22, 1, 0.36, 1) infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0;
  }
`;

const Mono = styled.span`
  font-size: 12px;
  font-weight: 800;
  line-height: 1;
  color: #dfe9ff;
  text-transform: uppercase;
`;

const More = styled.span`
  position: relative;
  z-index: 0;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  min-width: 22px;
  margin-left: -6px;
  /* the left side sits under the last logo: a little more room there so the + clears its ring */
  padding: 0 5px 0 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text};
  background: linear-gradient(180deg, rgba(${GREEN}, 0.28) 0%, rgba(${GREEN}, 0.12) 100%), ${({ theme }) => theme.colors.backgroundAlt};
  box-shadow: inset 0 0 0 1.5px rgba(${GREEN}, 0.85), 0 0 0 1.5px ${({ theme }) => theme.colors.backgroundAlt}, 0 3px 6px rgba(0, 0, 0, 0.45);
`;

function LogoImage({ logo, size, index }: { logo: NavLiveLogo; size: number; index: number }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [logo.src]);
  return (
    <Logo $size={size} $i={index} data-testid="nav-live-logo" title={logo.label} aria-label={`${logo.label} logo`} role="img">
      {logo.src && !failed ? (
        <img src={logo.src} alt="" width={size} height={size} decoding="async" referrerPolicy="no-referrer" onError={() => setFailed(true)} />
      ) : (
        <Mono>{logo.label.replace(/[^\p{L}\p{N}]/gu, "").slice(0, 1) || "?"}</Mono>
      )}
      {index === 0 && <Pulse aria-hidden />}
    </Logo>
  );
}

const LiveLogos: React.FC<{ live: NavLive; place: "bar" | "icon" }> = ({ live, place }) => {
  const { linkComponent } = useContext(MenuContext);
  const shown = live.logos.slice(0, LIVE_MAX_LOGOS);
  const count = live.logos.length + live.more;
  const size = LIVE_LOGO_PX[place];
  return (
    <Slot $place={place} data-testid="nav-live" data-count={count} data-place={place}>
      {shown.length > 0 && live.href && (
        <Cluster as={linkComponent} href={live.href} aria-label={live.ariaLabel} title={live.ariaLabel}>
          {shown.map((logo, i) => (
            <LogoImage key={logo.key} logo={logo} size={size} index={i} />
          ))}
          {live.more > 0 && <More data-testid="nav-live-more">+{live.more}</More>}
        </Cluster>
      )}
    </Slot>
  );
};

export default LiveLogos;
